import React, { useEffect, useMemo, useState } from "react";
import api from "../utils/api";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";

function todayYMD() {
  return new Date().toISOString().slice(0, 10);
}

function toYMD(val) {
  const s = String(val || "").trim();
  if (!s) return "";

  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

  const dmy = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(s);
  if (dmy) return `${dmy[3]}-${dmy[2]}-${dmy[1]}`;

  return "";
}

function toDMY(val) {
  const s = String(val || "").trim();
  if (!s) return "";

  const onlyDate = s.slice(0, 10);
  const ymd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(onlyDate);
  if (ymd) return `${ymd[3]}/${ymd[2]}/${ymd[1]}`;

  const dmy = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(s);
  if (dmy) return s;

  return "";
}

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function cleanStr(v) {
  return String(v ?? "").trim();
}

function pick(obj, keys, fallback = "") {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return fallback;
}

function toArray(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.results)) return data.results;
  return [];
}

function getEmpCode(emp) {
  return emp?.emp_code || emp?.empCode || emp?.code || "";
}

function getEmpName(emp) {
  return emp?.emp_name || emp?.empName || emp?.name || "";
}

export default function RecyclingEdit() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [date, setDate] = useState(todayYMD());
  const [receivedBy, setReceivedBy] = useState("");

  const [items, setItems] = useState([]);
  const [sources, setSources] = useState([]);
  const [employees, setEmployees] = useState([]);

  const [rows, setRows] = useState([
    {
      sourceType: "",
      sourceLineId: "",
      itemCode: "",
      itemName: "",
      remainingKg: 0,
      machineNo: "",
      operatorName: "",
      timeHour: "",
      resultKg: "",
      receiveDate: "",
    },
  ]);

  const [loading, setLoading] = useState(true);

  const itemNameByCode = useMemo(() => {
    const map = new Map();
    items.forEach((it) => {
      const code = pick(it, ["item_code", "itemCode"], "");
      const name = pick(it, ["item_name", "itemName"], "");
      if (code) map.set(code, name);
    });
    return map;
  }, [items]);

  const sourceByKey = useMemo(() => {
    const map = new Map();
    sources.forEach((s) => {
      map.set(`${s.sourceType}_${String(s.sourceLineId)}`, s);
    });
    return map;
  }, [sources]);

  const employeeOptions = useMemo(() => {
    return employees
      .map((emp) => {
        const empCode = getEmpCode(emp);
        const empName = getEmpName(emp);

        if (!empName) return null;

        return {
          value: empName,
          label: empName,
          searchText: `${empName} ${empCode}`.toLowerCase(),
        };
      })
      .filter(Boolean);
  }, [employees]);

  const sourceOptions = useMemo(() => {
    return sources.map((s) => {
      const value = `${s.sourceType}_${String(s.sourceLineId)}`;
      const label =
        s.label ||
        `${s.sourceType} | ${s.itemCode || ""} | ${s.machineNo || ""} | Rem: ${num(
          s.remainingKg
        ).toFixed(2)} KG`;

      return {
        value,
        label,
        searchText: `${label} ${s.itemCode || ""} ${s.itemName || ""} ${
          s.machineNo || ""
        } ${s.sourceType || ""}`.toLowerCase(),
      };
    });
  }, [sources]);

  const grandTotalKg = useMemo(
    () => rows.reduce((s, r) => s + num(r.resultKg), 0),
    [rows]
  );

  function updateRow(i, patch) {
    setRows((prev) => {
      const copy = [...prev];
      copy[i] = { ...copy[i], ...patch };
      return copy;
    });
  }

  function onSelectSource(i, value) {
    setRows((prev) => {
      const copy = [...prev];
      const src = sourceByKey.get(value);

      if (!src) {
        copy[i] = {
          ...copy[i],
          sourceType: "",
          sourceLineId: "",
          itemCode: "",
          itemName: "",
          remainingKg: 0,
          machineNo: "",
          resultKg: "",
        };
        return copy;
      }

      const code = src.itemCode || "";
      const rem = num(src.remainingKg);
      const oldResult = num(copy[i].resultKg);

      copy[i] = {
        ...copy[i],
        sourceType: src.sourceType,
        sourceLineId: String(src.sourceLineId),
        machineNo: src.machineNo || "",
        itemCode: code,
        itemName: src.itemName || itemNameByCode.get(code) || "",
        remainingKg: rem,
        resultKg: oldResult > rem ? "" : copy[i].resultKg,
      };

      return copy;
    });
  }

  function addRow() {
    setRows((p) => [
      ...p,
      {
        sourceType: "",
        sourceLineId: "",
        itemCode: "",
        itemName: "",
        remainingKg: 0,
        machineNo: "",
        operatorName: "",
        timeHour: "",
        resultKg: "",
        receiveDate: "",
      },
    ]);
  }

  function removeRow(i) {
    setRows((p) => (p.length === 1 ? p : p.filter((_, x) => x !== i)));
  }

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoading(true);

        const [iRes, c1Res, c2Res, empRes, hRes, lRes] = await Promise.all([
          api.get(`/api/items?type=recycle`),
          api.get(`/api/recycling/sources/crush1`),
          api.get(`/api/recycling/sources/crush2`),
          api.get(`/api/employees`),
          api.get(`/api/recycling/headers/${id}`),
          api.get(`/api/recycling/headers/${id}/lines`),
        ]);

        if (!alive) return;

        const itemsArr = toArray(iRes.data);
        const c1Arr = toArray(c1Res.data);
        const c2Arr = toArray(c2Res.data);
        const empArr = toArray(empRes.data);
        const allSources = [...c1Arr, ...c2Arr];

        setItems(itemsArr);
        setSources(allSources);
        setEmployees(empArr);

        const header = hRes.data || null;
        const linesArr = toArray(lRes.data);

        setDate(
          toDMY(pick(header, ["productionDate", "production_date"], "")) ||
            toDMY(todayYMD())
        );
        setReceivedBy(pick(header, ["receivedBy", "received_by"], ""));

        const mapped = linesArr.map((l) => {
          const sourceType = pick(l, ["source_type", "sourceType"], "");
          const sourceLineId = String(
            pick(l, ["source_line_id", "sourceLineId"], "")
          );
          const itemCode = pick(l, ["item_code", "itemCode"], "");
          const sourceKey = `${sourceType}_${sourceLineId}`;
          const src = allSources.find(
            (s) => `${s.sourceType}_${String(s.sourceLineId)}` === sourceKey
          );

          return {
            sourceType,
            sourceLineId,
            machineNo: pick(
              l,
              ["machine_no", "machineNo"],
              src?.machineNo || ""
            ),
            operatorName: pick(l, ["operator_name", "operatorName"], ""),
            timeHour: String(pick(l, ["hours", "time_hour", "timeHour"], "")),
            itemCode,
            itemName:
              pick(l, ["item_name", "itemName"], "") ||
              src?.itemName ||
              itemsArr.find(
                (x) => pick(x, ["item_code", "itemCode"], "") === itemCode
              )?.item_name ||
              "",
            remainingKg:
              num(src?.remainingKg) +
              num(pick(l, ["kg", "result_kg", "resultKg"], 0)),
            resultKg: String(
              num(pick(l, ["kg", "result_kg", "resultKg"], 0))
            ),
            receiveDate: toDMY(pick(l, ["receive_date", "receiveDate"], "")) || "",
          };
        });

        setRows(
          mapped.length
            ? mapped
            : [
                {
                  sourceType: "",
                  sourceLineId: "",
                  itemCode: "",
                  itemName: "",
                  remainingKg: 0,
                  machineNo: "",
                  operatorName: "",
                  timeHour: "",
                  resultKg: "",
                  receiveDate: "",
                },
              ]
        );
      } catch (e) {
        alert(
          e?.response?.data?.message ||
            e?.response?.data?.error ||
            e.message ||
            "Load failed"
        );
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [id]);

  async function saveUpdate() {
    try {
      const productionDate = toYMD(date);

      if (!productionDate) return alert("Date required");
      if (!cleanStr(receivedBy)) return alert("Received By required");

      for (const r of rows) {
        if (!r.sourceLineId) continue;
        if (!cleanStr(r.sourceType)) return alert("Source Type missing");
        if (!cleanStr(r.operatorName)) return alert("Operator Name required");
        if (num(r.resultKg) <= 0) return alert("Result KG must be > 0");
        if (num(r.resultKg) > num(r.remainingKg)) {
          return alert("Result KG cannot be greater than Remaining KG");
        }
      }

      const lines = rows
        .map((r) => ({
          sourceType: cleanStr(r.sourceType),
          sourceLineId: Number(r.sourceLineId || 0),
          machineNo: cleanStr(r.machineNo),
          operatorName: cleanStr(r.operatorName),
          timeHour: cleanStr(r.timeHour),
          itemCode: cleanStr(r.itemCode),
          itemName: cleanStr(r.itemName),
          remainingKg: num(r.remainingKg),
          resultKg: num(r.resultKg),
          receiveDate: cleanStr(r.receiveDate) ? toYMD(r.receiveDate) : null,
        }))
        .filter((x) => x.sourceType && x.sourceLineId && x.resultKg > 0);

      if (!lines.length) {
        return alert("Select at least one Source and enter KG");
      }

      await api.put(`/api/recycling/headers/${id}`, {
        productionDate,
        receivedBy: cleanStr(receivedBy),
        lines,
      });

      alert("Updated successfully");
      navigate("/recycling");
    } catch (e) {
      alert(
        e?.response?.data?.message ||
          e?.response?.data?.error ||
          e.message ||
          "Update failed"
      );
    }
  }

  const customSelectStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: "31px",
      height: "31px",
      border: "1px solid #ced4da",
      boxShadow: "none",
      borderRadius: "0.25rem",
    }),
    valueContainer: (provided) => ({
      ...provided,
      height: "31px",
      padding: "0 8px",
    }),
    input: (provided) => ({
      ...provided,
      margin: "0px",
      padding: "0px",
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: "31px",
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      padding: "4px",
    }),
    clearIndicator: (provided) => ({
      ...provided,
      padding: "4px",
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      marginTop: "6px",
      marginBottom: "6px",
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
      fontSize: "14px",
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#6c757d",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "#212529",
    }),
  };

  const filterSelectOption = (option, inputValue) => {
    const text =
      `${option.label} ${option.data.searchText || ""}`.toLowerCase();
    return text.includes(String(inputValue).toLowerCase());
  };

  if (loading) return <div className="p-3">Loading...</div>;

  return (
    <div className="container-fluid p-3">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h2 className="mb-0">Edit Recycling Entry (ID: {id})</h2>
        <div className="d-flex gap-2">
          <button className="btn btn-dark" onClick={saveUpdate}>
            Save Changes
          </button>
          <button
            className="btn btn-outline-secondary"
            onClick={() => navigate("/recycling")}
          >
            Close
          </button>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-body row g-3">
          <div className="col-md-3">
            <label className="form-label">Date</label>
            <input
              type="date"
              className="form-control"
              value={toYMD(date)}
              onChange={(e) => setDate(toDMY(e.target.value))}
            />
          </div>

          <div className="col-md-5">
            <label className="form-label">Received By</label>
            <Select
              options={employeeOptions}
              value={
                employeeOptions.find((opt) => opt.value === receivedBy) || null
              }
              onChange={(selected) =>
                setReceivedBy(selected ? selected.value : "")
              }
              placeholder="-- Select Employee --"
              isClearable
              isSearchable
              styles={customSelectStyles}
              noOptionsMessage={() => "No employee found"}
              filterOption={filterSelectOption}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>
        </div>
      </div>

      <div className="card">
        <div className="table-responsive">
          <table className="table table-bordered align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th style={{ width: 60 }}>SN</th>
                <th style={{ width: 320 }}>
                  Source (Crush 1 + Crush 2 Remaining)
                </th>
                <th style={{ width: 160 }}>Machine</th>
                <th style={{ width: 220 }}>Operator Name</th>
                <th style={{ width: 160 }}>Time / Hour</th>
                <th style={{ width: 140 }}>Item Code</th>
                <th>Item Name</th>
                <th style={{ width: 140 }}>Remaining KG</th>
                <th style={{ width: 140 }}>Result / KG</th>
                <th style={{ width: 170 }}>Receive Date</th>
                <th style={{ width: 180 }}>Action</th>
              </tr>
            </thead>

            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td className="text-center">{i + 1}</td>

                  <td className="align-middle">
                    <Select
                      options={sourceOptions}
                      value={
                        r.sourceType && r.sourceLineId
                          ? sourceOptions.find(
                              (opt) =>
                                opt.value === `${r.sourceType}_${r.sourceLineId}`
                            ) || null
                          : null
                      }
                      onChange={(selected) =>
                        onSelectSource(i, selected ? selected.value : "")
                      }
                      placeholder="-- Select --"
                      isClearable
                      isSearchable
                      styles={customSelectStyles}
                      noOptionsMessage={() => "No source found"}
                      filterOption={filterSelectOption}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </td>

                  <td>
                    <input
                      className="form-control form-control-sm"
                      value={r.machineNo}
                      readOnly
                    />
                  </td>

                  <td className="align-middle">
                    <Select
                      options={employeeOptions}
                      value={
                        employeeOptions.find(
                          (opt) => opt.value === r.operatorName
                        ) || null
                      }
                      onChange={(selected) =>
                        updateRow(i, {
                          operatorName: selected ? selected.value : "",
                        })
                      }
                      placeholder="-- Select Employee --"
                      isClearable
                      isSearchable
                      styles={customSelectStyles}
                      noOptionsMessage={() => "No employee found"}
                      filterOption={filterSelectOption}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </td>

                  <td>
                    <input
                      className="form-control form-control-sm"
                      value={r.timeHour}
                      onChange={(e) =>
                        updateRow(i, { timeHour: e.target.value })
                      }
                      placeholder="e.g. 11:30 / 8"
                    />
                  </td>

                  <td>
                    <input
                      className="form-control form-control-sm"
                      value={r.itemCode}
                      readOnly
                    />
                  </td>

                  <td>
                    <input
                      className="form-control form-control-sm"
                      value={r.itemName}
                      readOnly
                    />
                  </td>

                  <td>
                    <input
                      className="form-control form-control-sm"
                      value={num(r.remainingKg).toFixed(2)}
                      readOnly
                    />
                  </td>

                  <td>
                    <input
                      className="form-control form-control-sm"
                      value={r.resultKg}
                      onChange={(e) =>
                        updateRow(i, { resultKg: e.target.value })
                      }
                      placeholder="e.g. 200"
                    />
                  </td>

                  <td>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={toYMD(r.receiveDate)}
                      onChange={(e) =>
                        updateRow(i, { receiveDate: toDMY(e.target.value) })
                      }
                    />
                  </td>

                  <td>
                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => removeRow(i)}
                      >
                        Remove
                      </button>
                      <button
                        className="btn btn-sm btn-outline-dark"
                        onClick={addRow}
                      >
                        + Add
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="card-footer d-flex justify-content-between">
          <b>Total Records: {rows.length}</b>
          <b>Grand Total KG: {grandTotalKg.toFixed(2)}</b>
        </div>
      </div>
    </div>
  );
}